import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: false,
  error: null,
  initialized: false,

  clearError: () => set({ error: null }),

  initializeAuth: async () => {
    try {
      console.log('Initializing auth state...');
      
      // Get initial session
      const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      // Get user if session exists
      let user = null;
      let currentSession = initialSession;

      if (currentSession?.user) {
        const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        // Verify user has a profile
        if (authUser) {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', authUser.id)
            .single();

          if (!profileError && profile) {
            user = authUser;
          }
        }
      }

      // Try to refresh session if it exists but is expired
      if (currentSession?.expires_at && currentSession.expires_at <= Math.floor(Date.now() / 1000)) {
        console.log('Session expired, attempting refresh...');
        const { data: { session: refreshedSession }, error: refreshError } = 
          await supabase.auth.refreshSession();
        
        if (!refreshError && refreshedSession) {
          currentSession = refreshedSession;
          
          // Update user after refresh
          if (refreshedSession.user) {
            user = refreshedSession.user;
          }
        } else {
          // Session refresh failed - force clean state
          user = null;
          currentSession = null;
          localStorage.removeItem('sb-ijusthxxbsjwetoijhrm-auth-token');
          sessionStorage.clear();
          console.log('Session refresh failed, clearing auth state');
        }
      }
      
      console.log('Auth initialized:', { 
        hasUser: !!user, 
        hasSession: !!currentSession,
        expiresAt: currentSession?.expires_at
      });
      
      set({ 
        user,
        session: currentSession,
        initialized: true,
        error: null
      });

      // Set up auth state change listener
      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state changed:', event, 'Has session:', !!session);
        
        let user = null;
        if (session?.user) {
          const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
          if (!userError && authUser) {
            // Verify user has a profile
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', authUser.id)
              .single();

            if (!profileError && profile) {
              user = authUser;
            }
          }
        }
        
        if (event === 'SIGNED_OUT') {
          // Ensure we clear everything on sign out
          localStorage.removeItem('sb-ijusthxxbsjwetoijhrm-auth-token');
          sessionStorage.clear();
        }
        
        set({ 
          user, 
          session,
          initialized: true,
          error: null
        });
      });

    } catch (error) {
      console.error('Error initializing auth:', error);
      // Force clean state on initialization error
      set({ 
        user: null,
        session: null,
        error: error instanceof Error ? error.message : 'Failed to initialize auth',
        initialized: true
      });
      localStorage.removeItem('sb-ijusthxxbsjwetoijhrm-auth-token');
      sessionStorage.clear();
    }
  },

  signUp: async (email: string, password: string, displayName: string) => {
    set({ loading: true, error: null });
    try {
      // Create the auth user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('Failed to create user');

      // Create the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{ 
          id: authData.user.id, 
          display_name: displayName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);

      if (profileError) {
        // If profile creation fails, clean up the auth user
        await supabase.auth.signOut();
        throw profileError;
      }

      // Set the user in state
      set({ 
        user: authData.user,
        session: authData.session,
        error: null
      });

    } catch (error) {
      console.error('Error in signUp:', error);
      set({ error: error instanceof Error ? error.message : 'An error occurred during sign up' });
    } finally {
      set({ loading: false });
    }
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        set({ error: error.message || 'Invalid email or password' });
        set({ user: null, session: null });
        return;
      }
      
      // Verify user exists and has a profile
      if (data.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profileError || !profile) {
          await supabase.auth.signOut();
          set({ error: 'User profile not found' });
          set({ user: null, session: null });
          return;
        }

        // Set the user and session in state
        set({ 
          user: data.user,
          session: data.session,
          error: null
        });
      }
    } catch (error) {
      console.error('Error in signIn:', error);
      set({ error: error instanceof Error ? error.message : 'Invalid email or password' });
      // Ensure user is signed out on error
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error('Error signing out after failed login:', signOutError);
      }
      set({ user: null, session: null });
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    set({ loading: true, error: null });
    try {
      // Clear state and storage immediately so the UI updates
      set({
        user: null,
        session: null,
        error: null,
        loading: false,
        initialized: true
      });
      window.localStorage.clear();
      window.sessionStorage.clear();

      // Sign out from Supabase in the background (don't block on it)
      supabase.auth.signOut().catch(() => {});
    } catch (error) {
      console.error('Error in signOut:', error);
      set({
        user: null,
        session: null,
        error: null,
        loading: false,
        initialized: true
      });
      window.localStorage.clear();
      window.sessionStorage.clear();
    }
  },

  resetPassword: async (email: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'An error occurred during password reset' });
    } finally {
      set({ loading: false });
    }
  },
}));